import React, { useEffect, useState } from 'react';
import {
	Autocomplete,
	Box,
	CircularProgress,
	Divider,
	Grid,
	IconButton,
	Link,
	Stack,
	SxProps,
	TextField,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import useDocumentFiles from '../../hooks/useDocumentFiles';
import IDocumentFile from '../../interfaces/IDocumentFile';
import HAppRoutes from '../../../../app/features/routes/helpers/HAppRoutes';
import DocumentRoutes from '../../DocumentRoutes';
import { useNavigate } from 'react-router-dom';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';

import _ from 'lodash';
import styleVariables from '../../../../app/styles/styleVariables';
import Highlighter from 'react-highlight-words';
import HDocumentFile from '../../helpers/HDocumentFile';
import useDocumentFile from '../../hooks/useDocumentFile';
import { WarningOutlined } from '@mui/icons-material';
import CDocumentDownloadErrorSnackbar from '../../components/CDocumentDownloadErrorSnackbar';
import langDocumentFiles from '../../langs/langDocumentFiles';
import { useTranslation } from 'react-i18next';

const FDocumentSearchAutocompleteByName = () => {
	const { documents } = useDocumentFiles();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const { t } = useTranslation();

	const navigate = useNavigate();
	const [value, setValue] = useState<string | IDocumentFile | null>(null);
	const [inputValue, setInputValue] = useState('');
	const [searchWords, setSearchWords] = useState<string[]>([]);
	const [documentsFiltered, setDocumentFiltered] = useState<IDocumentFile[] | null>(null);
	const [documentsOptions, setDocumentOptions] = useState<IDocumentFile[]>([]);
	useEffect(() => {
		if (inputValue !== undefined) {
			setSearchWords(
				inputValue
					.split(' ')
					.filter((x) => x !== '')
					.map((word) => HDocumentFile.cleanWord(word)),
			);
		}
	}, [inputValue]);

	useEffect(() => {
		if (documents) {
			let documentsTmp = _.cloneDeep(documents);

			searchWords.forEach((word) => {
				documentsTmp = documentsTmp.filter((x) => HDocumentFile.cleanWord(x.name).includes(word));
			});
			setDocumentFiltered(documentsTmp);
		}
	}, [searchWords, documents]);

	useEffect(() => {
		if (documentsFiltered) {
			setDocumentOptions(documentsFiltered.slice(0, 5));
		}
	}, [documentsFiltered]);

	const sxHead: SxProps = {
		fontSize: '0.7rem',
		fontWeight: 600,
		color: styleVariables.greyColor,
	};

	// DOWNLOAD DOCUMENT
	const [selectDocumentId, setSelectDocumentId] = useState<number | null>(null);
	const { document: currentDocument, download } = useDocumentFile(selectDocumentId);
	const handleDownloadDocumentFile = (id: number) => {
		setSelectDocumentId(id);
	};
	const [downloadError, setDownloadError] = useState<Error | null>(null);

	useEffect(() => {
		if (currentDocument) {
			setDocumentOptions((prevData) =>
				prevData.map((x) => {
					if (x.id === currentDocument.id) {
						x.isDownloading = true;
					}
					return x;
				}),
			);
			download()
				.then(() => {
					setDocumentOptions((prevData) =>
						prevData.map((x) => {
							if (x.id === currentDocument.id) {
								x.isDownloading = false;
							}
							return x;
						}),
					);
				})
				.catch((e) => {
					setDownloadError(e);
					setDocumentOptions((prevData) =>
						prevData.map((x) => {
							if (x.id === currentDocument.id) {
								x.isDownloadingError = true;
							}
							return x;
						}),
					);
				})
				.finally(() => {
					setSelectDocumentId(null);
				});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentDocument]);

	const handleClickAll = () => {
		if (searchWords.length > 0) {
			navigate(
				HAppRoutes.getPath(DocumentRoutes.documents_search.tag) + '?keywords=' + encodeURIComponent(inputValue),
			);
		} else {
			navigate(HAppRoutes.getPath(DocumentRoutes.documents_search.tag));
		}
	};

	return (
		<>
			{documents ? (
				<>
					<Autocomplete
						className="filter-document-search-autocomplete"
						filterOptions={(x) => x}
						options={documentsOptions}
						getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
						freeSolo
						autoComplete
						includeInputInList
						filterSelectedOptions
						disableCloseOnSelect
						onKeyDown={(e) => {
							if (['Enter'].includes(e.key)) {
								e.preventDefault();
								handleClickAll();
							}
						}}
						inputValue={inputValue}
						sx={{
							borderRadius: '0.25rem',

							'& fieldset': {
								border: 'none',
								borderRadius: '0.25rem',
								fontSize: '13px',
							},
							'& .MuiAutocomplete-clearIndicator': {
								visibility: 'visible',
								color: theme.palette.primary.main,
							},
						}}
						value={value}
						onChange={(_event, newValue) => {
							setValue(newValue);
						}}
						onInputChange={(_event, newInputValue) => {
							setInputValue(newInputValue);
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								variant="outlined"
								size="small"
								label={t(langDocumentFiles.search_autocomplete_placeholder.tag)}
							/>
						)}
						groupBy={() => ''}
						renderGroup={(line) => (
							<Box key={line.key}>
								<Box sx={{ padding: '10px' }}>
									<Grid container spacing={2} alignItems="center">
										{!isMobile ? (
											<>
												<Grid item sm={8} xs={12} sx={sxHead}>
													{t(langDocumentFiles.search_autocomplete_header_document.tag)}
												</Grid>
												<Grid item sm={3} xs={8} sx={sxHead}>
													{t(langDocumentFiles.search_autocomplete_header_fund.tag)}
												</Grid>
												<Grid item sm={1} xs={4} sx={{ ...sxHead, textAlign: 'center' }}>
													{t(langDocumentFiles.search_autocomplete_header_download.tag)}
												</Grid>
											</>
										) : (
											<></>
										)}
										<>{line.children}</>
									</Grid>
								</Box>

								<Divider />
								<Box sx={{ padding: '10px' }}>
									<Box>
										<Link
											sx={{
												fontWeight: 600,
												textDecoration: 'none',
												'&:hover': {
													textDecoration: 'underline',
												},
											}}
											onClick={() => handleClickAll()}
										>
											<Stack direction="row" spacing={1} alignItems="center">
												<Box>
													{t(langDocumentFiles.see_x_files.tag, {
														count: documentsFiltered?.length ?? 0,
													})}
												</Box>
												<AppCustomIcon tag="arrow-right" style={{ verticalAlign: 'middle' }} />
											</Stack>
										</Link>
									</Box>
								</Box>
							</Box>
						)}
						renderOption={(_props, option: IDocumentFile) => (
							<Grid item container sm={12} xs={12} key={option.id}>
								<Grid item sm={8} xs={12}>
									<Link
										sx={{
											textDecoration: 'none',
											'&:hover': {
												textDecoration: 'underline',
											},
										}}
										onClick={() =>
											navigate(
												HAppRoutes.getPath(DocumentRoutes.document.tag, {
													id: option.id.toString(),
												}),
											)
										}
									>
										<Highlighter
											searchWords={searchWords}
											autoEscape
											textToHighlight={option.name}
											sanitize={HDocumentFile.cleanWord}
											highlightStyle={{
												fontWeight: 700,
												backgroundColor: '#FFF',
											}}
										/>
									</Link>
								</Grid>
								{!isMobile ? (
									<>
										<Grid item sm={3} xs={8}>
											{option.fund?.name}
										</Grid>
										<Grid item sm={1} xs={4} sx={{ textAlign: 'center' }}>
											<IconButton
												onClick={() => handleDownloadDocumentFile(option.id)}
												disabled={option.isDownloading || option.isDownloadingError}
											>
												{option.isDownloadingError ? (
													<WarningOutlined />
												) : option.isDownloading ? (
													<CircularProgress color="secondary" size={26} />
												) : (
													<AppCustomIcon tag="download" />
												)}
											</IconButton>
										</Grid>
									</>
								) : (
									<></>
								)}
							</Grid>
						)}
					/>
					<CDocumentDownloadErrorSnackbar
						downloadError={downloadError}
						onClose={() => setDownloadError(null)}
					/>
				</>
			) : (
				<></>
			)}
		</>
	);
};

export default FDocumentSearchAutocompleteByName;
